import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import Loading from "@/components/Loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";

import type { InfoHubSubjectRow, SubjectActions } from "../../types";
import { createColumns, type CountryOption, type SubjectColumnDef } from "./columns";

const DEFAULT_PAGE_SIZE = 10;

interface SubjectsDataTableProps {
  data: InfoHubSubjectRow[];
  isFetching?: boolean;
  actions: SubjectActions;
  isAllCountries?: boolean;
  countries?: CountryOption[];
}

export const SubjectsDataTable = ({
  data,
  isFetching = false,
  actions,
  isAllCountries = false,
  countries = [],
}: SubjectsDataTableProps) => {
  const { t } = useTranslation();
  // Default: latest created first; the Created header toggles earliest/latest.
  const [sorting, setSorting] = useState<SortingState>([{ id: "created", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const columns = useMemo(
    () => createColumns(t, actions, isAllCountries, countries),
    [t, actions, isAllCountries, countries]
  );

  useEffect(() => {
    setPagination(current => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, [sorting]);

  const table = useReactTable({
    columns,
    data,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: row => String(row.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { pagination, sorting },
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;
  const getMeta = (columnDef: SubjectColumnDef) => columnDef.meta;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={cn("p-3", getMeta(header.column.columnDef)?.headerClassName)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("infoHub.table.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={cn("align-top", getMeta(cell.column.columnDef)?.cellClassName)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      <DataTablePagination table={table} />
    </div>
  );
};

export default SubjectsDataTable;
